import { STRAPI_URL } from "../env";
import {
  appQueryClient,
  fetchWithCache,
  invalidateAppQueries,
} from "./query-cache";

export async function fetchViewConfiguration(
  type: "dashboard" | "overview" | "project-common"
): Promise<
  | {
      tabs: {
        id: number;
        title: string;
        rows: {
          id: number;
          columnCount: number;
          columns: {
            columnIndex: number;
            componentId: string;
          }[];
        }[];
      }[];
      displayTabs?: boolean;
      canEdit?: boolean;
      isAiEnabled?: boolean;
      type: "dashboard" | "overview" | "project-common";
    }
  | undefined
> {
  return fetchWithCache({
    queryKey: ["view-configuration", type],
    staleTime: 15 * 60 * 1000,
    fallback: appQueryClient.getQueryData<
      Awaited<ReturnType<typeof fetchViewConfiguration>>
    >(["view-configuration", type]),
    queryFn: async () => {
      const response = await fetch(`${STRAPI_URL}/view-configuration/${type}`, {
        credentials: "include",
      });

      if (response.status >= 400) {
        throw new Error(`Fetch failed with status ${response.status}.`);
      }

      return (await response.json()) as {
        tabs: {
          id: number;
          title: string;
          rows: {
            id: number;
            columnCount: number;
            columns: {
              columnIndex: number;
              componentId: string;
            }[];
          }[];
        }[];
        displayTabs?: boolean;
        canEdit?: boolean;
        isAiEnabled?: boolean;
        type: "dashboard" | "overview" | "project-common";
      };
    },
  });
}

export async function createViewConfiguration(configuration: {
  tabs: {
    id: number;
    title: string;
    rows: {
      id: number;
      columnCount: number;
      columns: {
        columnIndex: number;
        componentId: string;
      }[];
    }[];
  }[];
  displayTabs?: boolean;
  canEdit?: boolean;
  isAiEnabled?: boolean;
  type: "dashboard" | "overview" | "project-common";
}): Promise<
  | {
      tabs: {
        id: number;
        title: string;
        rows: {
          id: number;
          columnCount: number;
          columns: {
            columnIndex: number;
            componentId: string;
          }[];
        }[];
      }[];
      displayTabs?: boolean;
      canEdit?: boolean;
      isAiEnabled?: boolean;
      type: "dashboard" | "overview" | "project-common";
    }
  | undefined
> {
  try {
    const response = await fetch(`${STRAPI_URL}/view-configuration`, {
      credentials: "include",
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(configuration),
    });

    if (response.status >= 400) {
      throw new Error(`Fetch failed with status ${response.status}.`);
    }

    await invalidateAppQueries(["view-configuration"]);

    const json = (await response.json()) as {
      tabs: {
        id: number;
        title: string;
        rows: {
          id: number;
          columnCount: number;
          columns: {
            columnIndex: number;
            componentId: string;
          }[];
        }[];
      }[];
      displayTabs?: boolean;
      canEdit?: boolean;
      isAiEnabled?: boolean;
      type: "dashboard" | "overview" | "project-common";
    };

    return json;
  } catch {
    return;
  }
}

export function mapViewConfiguration(viewConfiguration?: {
  tabs: {
    id: number;
    title: string;
    rows: {
      id: number;
      columnCount: number;
      columns: {
        columnIndex: number;
        componentId: string;
      }[];
    }[];
  }[];
  displayTabs?: boolean;
  canEdit?: boolean;
  isAiEnabled?: boolean;
  type: "dashboard" | "overview" | "project-common";
}) {
  return {
    canEdit: viewConfiguration?.canEdit ?? true,
    displayTabs: viewConfiguration?.displayTabs ?? true,
    tabs:
      viewConfiguration?.tabs && viewConfiguration.tabs.length > 0
        ? viewConfiguration.tabs
        : [
            {
              id: 0,
              title: "Tab 1",
              rows: [
                {
                  id: 0,
                  columnCount: 1,
                  columns: [],
                },
              ],
            },
          ],
  };
}
